import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, ChevronLeft, ChevronRight, RotateCcw, Lightbulb, Sparkles, Code2, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CONCEPT_LIBRARY, CODE_EXAMPLES, conceptForChapter } from '../data/programmingConcepts';

// Tiny circular quota indicator — fills clockwise as the student burns
// through their 10 daily AI actions.  Hides itself for exempt admin roles.
const QuotaRing = () => {
  const [q, setQ] = useState(null);
  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API}/api/llm-quota`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (r.ok) setQ(await r.json());
      } catch { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);
  if (!q || q.exempt) return null;
  const pct = Math.min(100, (q.count / q.limit) * 100);
  const stroke = pct >= 100 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#10b981';
  const c = 2 * Math.PI * 14; // circumference
  return (
    <div className="flex items-center gap-2" data-testid="concept-quota-ring" title={`You have used ${q.count} of ${q.limit} AI-assisted actions today.`}>
      <svg width="34" height="34" viewBox="0 0 34 34" className="-rotate-90">
        <circle cx="17" cy="17" r="14" stroke="rgba(148, 163, 184, 0.25)" strokeWidth="3" fill="none" />
        <motion.circle
          cx="17" cy="17" r="14"
          stroke={stroke} strokeWidth="3" fill="none" strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c - (pct / 100) * c }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </svg>
      <div className="text-[10px] leading-tight">
        <div className="font-semibold" style={{ color: stroke }}>{q.remaining}/{q.limit}</div>
        <div className="text-muted-foreground">AI today</div>
      </div>
    </div>
  );
};

// Character image → asset path.  These are 3D-render style portraits stored in
// /public/characters/*.png.  Mapped from concept.character to the closest match.
const CHARACTER_IMAGES = {
  mentor: '/characters/mentor.png',    // warm elderly-male professor with glasses
  scientist: '/characters/mentor.png', // same warm mentor (glasses = scientist too)
  teacher: '/characters/teacher.png',  // Indian teacher in green sari
  coder: '/characters/teacher.png',    // energetic teacher covers coding scenes
};
// Character → server-side voice enum used by /api/concept/narrate.
const CHARACTER_VOICE = {
  mentor: 'mentor', scientist: 'scientist', teacher: 'teacher', coder: 'coder',
};

const API = process.env.REACT_APP_BACKEND_URL;

// A framed 3D portrait with a subtle bobbing motion when the scene is speaking.
const CharacterPortrait = ({ mood = 'mentor', talking = false }) => {
  const src = CHARACTER_IMAGES[mood] || CHARACTER_IMAGES.mentor;
  return (
    <div className="relative w-full aspect-square rounded-2xl overflow-hidden shadow-lg border border-border/50" data-testid="concept-character">
      {/* soft halo behind */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-200/40 via-transparent to-cyan-300/40" aria-hidden />
      <motion.img
        src={src}
        alt="Concept mentor"
        className="relative w-full h-full object-cover"
        animate={talking ? { y: [0, -3, 0], scale: [1, 1.01, 1] } : { y: 0, scale: 1 }}
        transition={talking ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.3 }}
        draggable={false}
      />
      {/* speaking indicator ring */}
      {talking && (
        <motion.div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          initial={{ opacity: 0.4 }}
          animate={{ opacity: [0.4, 0.9, 0.4] }}
          transition={{ duration: 1.4, repeat: Infinity }}
          style={{ boxShadow: 'inset 0 0 0 3px rgba(139, 92, 246, 0.55)' }}
        />
      )}
    </div>
  );
};

// Main tab body.  Auto-plays through the scenes; user can pause / step / restart.
export default function AnimatedConceptExplainer({ chapter, chapterIndex, language = 'python' }) {
  const conceptId = conceptForChapter(chapter, chapterIndex);
  const concept = CONCEPT_LIBRARY[conceptId] || CONCEPT_LIBRARY.variables;
  const code = (CODE_EXAMPLES[conceptId] && CODE_EXAMPLES[conceptId][language]) || CODE_EXAMPLES[conceptId]?.python || '';

  const [sceneIdx, setSceneIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [typed, setTyped] = useState('');
  const [audioOn, setAudioOn] = useState(true);
  const [audioReady, setAudioReady] = useState(false);
  const audioRef = useRef(null);

  const scene = concept.scenes[sceneIdx];
  const voiceChar = CHARACTER_VOICE[concept.character] || 'mentor';
  const audioSrc = `${API}/api/concept/narrate?concept_id=${conceptId}&scene_index=${sceneIdx}&character=${voiceChar}`;

  // Auto-advance while playing — waits for audio to finish if audio is on,
  // otherwise falls back to a fixed timer.
  useEffect(() => {
    if (!playing) return;
    if (audioOn) return; // audio 'ended' event triggers advance instead
    const t = setTimeout(() => {
      setSceneIdx((i) => (i + 1 < concept.scenes.length ? i + 1 : i));
    }, 4600);
    return () => clearTimeout(t);
  }, [sceneIdx, playing, audioOn, concept.scenes.length]);

  // Reset when chapter OR concept changes.  Some chapters map to the same
  // concept (e.g. chapter 1 & 3 both → 'variables') so we key on BOTH
  // chapter_id and conceptId to guarantee a reset.  Without this the
  // concept tab appeared frozen on the previous chapter.
  useEffect(() => {
    setSceneIdx(0);
    setPlaying(true);
    setTyped('');
  }, [chapter?.chapter_id, chapterIndex, conceptId]);

  // Typewriter effect for scene text.
  useEffect(() => {
    setTyped('');
    if (!scene?.text) return;
    let i = 0;
    const iv = setInterval(() => {
      i += 1;
      setTyped(scene.text.slice(0, i));
      if (i >= scene.text.length) clearInterval(iv);
    }, 22);
    return () => clearInterval(iv);
  }, [scene?.text]);

  // Audio play/pause/end wiring — reloads when src changes.
  useEffect(() => {
    if (!audioRef.current) return;
    setAudioReady(false);
    // Force the <audio> element to reload the new src (Safari & Chrome quirk).
    try { audioRef.current.load(); } catch { /* ignore */ }
    if (audioOn && playing) {
      const p = audioRef.current.play();
      if (p && p.catch) p.catch(() => { /* browser autoplay policy — ignore */ });
    } else {
      audioRef.current.pause();
    }
  }, [audioSrc, playing, audioOn]);

  const handleAudioEnded = () => {
    if (playing) {
      setSceneIdx((i) => (i + 1 < concept.scenes.length ? i + 1 : i));
    }
  };

  const atEnd = sceneIdx >= concept.scenes.length - 1;
  const progress = ((sceneIdx + 1) / concept.scenes.length) * 100;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gradient-to-br from-indigo-500/5 via-background to-emerald-500/5" data-testid="concept-tab-content">
      {/* Hidden audio element — drives narration */}
      <audio
        ref={audioRef}
        src={audioOn ? audioSrc : undefined}
        preload="auto"
        onEnded={handleAudioEnded}
        onCanPlay={() => setAudioReady(true)}
        data-testid="concept-audio"
      />
      {/* Header + progress */}
      <div className="px-6 pt-5 pb-3 border-b border-border/60 bg-background/60 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold truncate">{concept.title}</h3>
            <p className="text-xs text-muted-foreground truncate">
              {chapter?.title ? `Chapter: ${chapter.title} · ` : ''}Scene {sceneIdx + 1} of {concept.scenes.length}{audioOn && audioReady ? ' · 🔊 Narrating' : ''}
            </p>
          </div>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide">{language}</Badge>
          <QuotaRing />
        </div>
        <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-emerald-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      {/* Stage */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto px-6 py-6 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6">
          {/* Character portrait */}
          <div className="flex flex-col items-center">
            <div className="w-full max-w-[240px]">
              <CharacterPortrait mood={concept.character} talking={playing} />
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-wide text-muted-foreground">
              {concept.character === 'teacher' || concept.character === 'coder' ? 'Priya Ma\'am' : 'Prof. Sharma'}
            </div>
          </div>

          {/* Speech bubble + scene */}
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={sceneIdx}
                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35 }}
                className="rounded-2xl bg-white dark:bg-slate-900 border border-border shadow-lg p-5"
                data-testid={`concept-scene-${sceneIdx}`}
              >
                {/* speech triangle */}
                <div className="absolute -left-3 top-8 w-4 h-4 rotate-45 bg-white dark:bg-slate-900 border-l border-b border-border hidden md:block" />
                <div className="flex items-start gap-3">
                  <div className="text-4xl leading-none select-none">{scene.emoji}</div>
                  <div className="flex-1">
                    <p className="text-[15px] leading-relaxed text-foreground">
                      {typed}
                      {typed.length < scene.text.length && (
                        <span className="inline-block w-2 h-4 bg-indigo-500 ml-0.5 align-middle" style={{ animation: 'concept-caret 0.6s steps(2) infinite' }} />
                      )}
                    </p>
                    {scene.tip && (
                      <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2">
                        <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <span className="text-[13px] text-amber-700 dark:text-amber-300">{scene.tip}</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Code sample once user reaches end */}
            {atEnd && code && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-5 rounded-xl overflow-hidden border border-border bg-[#0d1117]"
                data-testid="concept-code-sample"
              >
                <div className="flex items-center gap-2 px-3 py-2 bg-[#161b22] text-[11px] text-slate-300 border-b border-slate-800">
                  <Code2 className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="font-mono">example.{language}</span>
                  <span className="ml-auto text-[10px] text-slate-500">Same idea, in your language</span>
                </div>
                <pre className="text-emerald-300 text-[12.5px] leading-6 p-4 font-mono whitespace-pre overflow-auto">{code}</pre>
              </motion.div>
            )}

            {/* Summary at very end */}
            {atEnd && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-5 rounded-xl bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-cyan-500/10 border border-emerald-500/30 p-4"
              >
                <div className="text-[11px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400 font-semibold mb-1">Takeaway</div>
                <p className="text-[14px] leading-relaxed">{concept.summary}</p>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="border-t border-border/60 bg-background/70 backdrop-blur px-6 py-3 flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setAudioOn((a) => !a)}
          title={audioOn ? 'Mute narration' : 'Turn on narration'}
          data-testid="concept-audio-toggle"
        >
          {audioOn ? <Volume2 className="w-3.5 h-3.5 mr-1 text-emerald-500" /> : <VolumeX className="w-3.5 h-3.5 mr-1 text-muted-foreground" />}
          {audioOn ? 'Voice on' : 'Voice off'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setSceneIdx(0); setPlaying(true); }}
          data-testid="concept-restart-btn"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restart
        </Button>
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSceneIdx((i) => Math.max(0, i - 1))}
          disabled={sceneIdx === 0}
          data-testid="concept-prev-btn"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={() => setPlaying((p) => !p)}
          data-testid="concept-play-btn"
        >
          {playing ? <><Pause className="w-3.5 h-3.5 mr-1" />Pause</> : <><Play className="w-3.5 h-3.5 mr-1" />Play</>}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSceneIdx((i) => Math.min(concept.scenes.length - 1, i + 1))}
          disabled={atEnd}
          data-testid="concept-next-btn"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </Button>
      </div>

      <style>{`@keyframes concept-caret { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
    </div>
  );
}
